import { Box, Typography } from "@mui/material";
import React from "react";
import { tokens, eyebrowSx, mono, numericSx } from "../../../theme";
import { tints } from "../../../theme/overrides";
import SectionCard from "../../reusableComponents/sectionCard/sectionCard";
import EmptyState from "../../reusableComponents/emptyState/emptyState";
import MiniSpinner from "../../reusableComponents/miniSpinner/miniSpinner";
import { RankEntry } from "./salesRepViewModel";

/**
 * The refreshed sales-rep side panel's mini ranking card: position, name,
 * proportional track and value per rep, with the current rep's row lifted
 * ("You" chip, gained-green fill). Pure presentation — the entries come from
 * the same payloads the full ranking BarCharts plot.
 */
interface RankMiniListProps {
  title: string;
  /** Small mono context tag in the card header. Omit rather than guess a range. */
  rangeLabel?: string;
  entries: RankEntry[];
  formatValue: (value: number) => string;
  loading?: boolean;
  /** Body copy for the empty case — name the surface truthfully. */
  emptyBody: string;
}

const RankMiniList = ({
  title,
  rangeLabel,
  entries,
  formatValue,
  loading,
  emptyBody,
}: RankMiniListProps) => {
  const max = entries.reduce((m, e) => Math.max(m, e.value), 0);

  return (
    <SectionCard
      title={title}
      action={
        rangeLabel ? (
          <Typography sx={{ ...eyebrowSx }}>{rangeLabel}</Typography>
        ) : undefined
      }
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 140,
          }}
        >
          <MiniSpinner />
        </Box>
      ) : entries.length === 0 ? (
        <EmptyState kind="empty" title="No ranking data" body={emptyBody} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {entries.map((e) => (
            <Box
              key={`${e.rank}-${e.name}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "18px 110px minmax(0,1fr) 44px",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontFamily: mono,
                  fontSize: 10.5,
                  color: tokens.color.faint,
                  ...numericSx,
                }}
              >
                {e.rank}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: 12.5,
                    color: e.isYou ? tokens.color.ink : tokens.color.body,
                    fontWeight: e.isYou ? 600 : 400,
                  }}
                >
                  {e.name}
                </Typography>
                {e.isYou && (
                  <Box
                    component="span"
                    sx={{
                      flex: "none",
                      backgroundColor: tokens.color.positive.bg,
                      color: tokens.color.positive.text,
                      borderRadius: `${tokens.radius.pill}px`,
                      padding: "1px 7px",
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    You
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  height: 12,
                  borderRadius: "4px",
                  backgroundColor: tints.neutralChip,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    borderRadius: "4px",
                    width: `${max > 0 ? (e.value / max) * 100 : 0}%`,
                    backgroundColor: e.isYou
                      ? tokens.chart.gainLost.gained
                      : tokens.color.borderHover,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: tokens.color.ink,
                  textAlign: "right",
                  ...numericSx,
                }}
              >
                {formatValue(e.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

export default RankMiniList;
